/**
 * Shared human-readable display names for AS-IS signal_engine parameter keys.
 * Mirrors constants/parameter_labels.py in the backend.
 */

export const PARAMETER_DISPLAY_NAMES: Record<string, string> = {
  // On-Page: parameter groups
  serp_snippet_optimization: 'SERP Snippet Optimization',
  content_structure_hierarchy: 'Content Structure & Hierarchy',
  accessibility_optimization: 'Accessibility Optimization',
  url_page_signals: 'URL & Page-Level Signals',
  // On-Page: sub-parameters
  title_tag_presence_optimization: 'Title Tag Presence & Optimization',
  meta_description_presence_quality: 'Meta Description Presence & Quality',
  rich_snippet_eligibility: 'Rich Snippet Eligibility',
  schema_markup_implementation: 'Schema Markup Implementation',
  h1_presence_uniqueness: 'H1 Presence & Uniqueness',
  h2_h6_hierarchy: 'H2–H6 Hierarchy',
  content_depth_completeness: 'Content Depth & Completeness',
  content_freshness: 'Content Freshness',
  image_alt_text: 'Image Alt Text',
  image_file_naming: 'Image File Naming',
  image_relevance: 'Image Relevance',
  lazy_loading_issues: 'Lazy Loading Issues',
  accessibility_compliance: 'Accessibility Compliance',
  url_structure: 'URL Structure',
  url_length: 'URL Length',
  canonical_alignment: 'Canonical Alignment',
  parameterized_urls: 'Parameterised URLs',
  duplicate_urls: 'Duplicate URLs',
  // Off-Page: parameter groups
  domain_authority_trust: 'Domain Authority & Trust Signals',
  backlink_relevance: 'Backlink Relevance & Topic Alignment',
  anchor_text_balance: 'Anchor Text Profile & Risk Balance',
  brand_entity_authority: 'Brand Mentions & Entity Authority',
  link_growth_stability: 'Link Growth Stability',
  // Off-Page: sub-parameters
  referring_domains_count: 'Referring Domains Count',
  authority_score: 'Authority Score',
  spam_score: 'Spam Score',
  toxic_score: 'Toxic Score',
  anchor_text_distribution: 'Anchor Text Distribution',
  exact_match_anchor_frequency: 'Exact Match Anchor Frequency',
  brand_citations: 'Brand Citations',
  entity_coverage: 'Entity Coverage',
  // Technical: parameter groups
  crawl_indexation: 'Crawl & Indexation',
  canonicalization_duplicate: 'Canonicalization & Duplicate Control',
  page_experience_performance: 'Page Experience Performance',
  search_rendering_accessibility: 'Search Rendering Accessibility',
  ai_llm_crawl: 'AI & LLM Crawl',
  // Technical: sub-parameters
  xml_sitemap_health: 'XML Sitemap Health',
  robots_txt_configuration: 'Robots.txt Configuration',
  indexed_vs_non_indexed_pages: 'Indexed vs Non-Indexed Pages',
  crawl_budget_efficiency: 'Crawl Budget Efficiency',
  orphan_urls: 'Orphan URLs',
  canonical_tag_implementation: 'Canonical Tag Implementation',
  http_vs_https_duplicates: 'HTTP vs HTTPS Duplicates',
  trailing_slash_issues: 'Trailing Slash Issues',
  lcp_performance: 'LCP Performance',
  cls_stability: 'CLS Stability',
  mobile_vs_desktop_cwv: 'Mobile vs Desktop Core Web Vitals',
  javascript_rendering_issues: 'JavaScript Rendering Issues',
  delayed_lazy_content: 'Delayed Lazy Content',
  hidden_content: 'Hidden Content',
  client_side_rendering_risks: 'Client-Side Rendering Risks',
  critical_resource_blocking: 'Critical Resource Blocking',
  llm_txt_presence_rules: 'LLM.txt Presence & Rules',
  ai_crawler_permissions: 'AI Crawler Permissions',
  // On-Page: additional sub-parameters
  ctr_readiness_signals: 'CTR Readiness Signals',
  keyword_in_url: 'Keyword in URL',
  title_meta_duplication: 'Title & Meta Duplication',
  pixel_length_issues: 'Pixel Length Issues',
  heading_order_issues: 'Heading Order Issues',
  first_100_words_relevance: 'First 100 Words Relevance',
  content_sectioning: 'Content Sectioning',
  // Off-Page: additional sub-parameters
  follow_vs_nofollow_backlinks: 'Follow vs Nofollow Backlinks',
  consistency_of_brand_name_usage: 'Brand Name Consistency',
  industry_mentions: 'Industry Mentions',
  link_source_quality_distribution: 'Link Source Quality',
  content_usage_signals: 'Content Usage Signals',
  // Technical: additional sub-parameters
  ai_indexing_exposure: 'AI Indexing Exposure',
};

export function getParamLabel(key: string | null | undefined): string {
  if (!key) return 'SEO Parameter';
  return (
    PARAMETER_DISPLAY_NAMES[key] ??
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
